import { styled, Button, Typography, Chip } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { localization } from '../constants';
import { stateContext } from '../contexts';
import { usePackageSelectionLogic } from '../hooks';
import type { Package } from '../types';
import { ModalContent } from './ModalContent';
import { PackagePicture } from './PackagePicture';
import { PackagesLoader } from '~/shared/components/PackagesLoader';

const Filters = styled('section')({
  width: '100%',
});

const Packages = styled('div')(({ theme }) => ({
  display: 'grid',
  overflow: 'auto',
  rowGap: theme.spacing(3),
}));

const PackageButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  justifyContent: 'stretch',
  border: '1px solid',
  padding: theme.spacing(2),
  borderColor: theme.palette.grey[200],
  '&:hover': {
    border: '1px solid',
    borderColor: theme.palette.secondary.main,
  },
}));

const PackageLabel = styled(Typography)({
  textAlign: 'left',
  textTransform: 'none',
});

export function PackageSelection() {
  const { getTestIdProps } = useTestId();
  const { setPackage, selectedPackage } = useContext(stateContext);
  const [filter, setFilter] = useState(0);
  const { formatMessage } = useIntl();

  const { packages, categories, isLoading } = usePackageSelectionLogic(filter);

  useEffect(() => {
    if (categories.length === 1) {
      const categoryTypeId = categories[0]?.typeId ?? 0;
      setFilter(categoryTypeId);
    }
  }, [categories]);

  const onClickSetPackage = (autoshipPackage: Package) => () => setPackage(autoshipPackage);

  const onClickSetFilter = (categoryFilter: number) => () => setFilter(categoryFilter);

  return (
    <ModalContent dataId="package-selection">
      {isLoading ? (
        <PackagesLoader />
      ) : (
        <>
          <Filters>
            {categories.length > 1 && (
              <Chip
                clickable
                label={formatMessage({
                  id: 'all',
                  defaultMessage: localization.all,
                })}
                size="small"
                sx={{ marginRight: 2 }}
                variant={filter > 0 ? 'outlined' : undefined}
                onClick={onClickSetFilter(0)}
              />
            )}
            {categories.map((category) => (
              <Chip
                key={category.typeId}
                clickable
                label={category.description}
                size="small"
                variant={filter === category.typeId ? undefined : 'outlined'}
                sx={{ marginRight: 2 }}
                {...getTestIdProps(`${category.category}`)}
                onClick={onClickSetFilter(category.typeId)}
              />
            ))}
          </Filters>

          <Packages {...getTestIdProps('packages')}>
            {packages.map((item) => (
              <PackageButton
                key={item.packageId}
                disableRipple
                fullWidth
                startIcon={<PackagePicture url={item.imageSrc} />}
                sx={
                  item.packageId === selectedPackage?.packageId
                    ? { borderColor: 'primary.main' }
                    : undefined
                }
                {...getTestIdProps('package-button')}
                type="button"
                variant="outlined"
                onClick={onClickSetPackage(item)}
              >
                <PackageLabel
                  variant="body1"
                  {...getTestIdProps('package-label')}
                >
                  {item.description}
                </PackageLabel>
              </PackageButton>
            ))}
          </Packages>
        </>
      )}
    </ModalContent>
  );
}
